import { ELEMENT_CONTRACT_ADDRESS } from '@components/RichTextPlate/plugins/createContractAddressPlugin'
import { ELEMENT_MENTION_TICKER } from '@components/RichTextPlate/plugins/createTickerMentionPlugin'
import { TCodeBlockElement } from '@udecode/plate-code-block'
import { CodeBlockPlugin, CodeLinePlugin } from '@udecode/plate-code-block/react'
import { TMentionElement } from '@udecode/plate-mention'
import { MentionPlugin } from '@udecode/plate-mention/react'

import {
    TChannelMentionElement,
    TMentionEmoji,
    TMentionTicker,
} from '../../components/plate-ui/autocomplete/types'
import { ELEMENT_MENTION_CHANNEL } from '../../plugins/createChannelPlugin'
import { ELEMENT_MENTION_EMOJI } from '../../plugins/createEmojiPlugin'

export interface NodeTypes {
    paragraph: string
    block_quote: string
    code_block: string
    code_line: string
    link: string
    ul_list: string
    ol_list: string
    li: string
    listItem: string
    lic: string
    heading: {
        1: string
        2: string
        3: string
        4: string
        5: string
        6: string
    }
    emphasis_mark: string
    strong_mark: string
    underline_mark: string
    delete_mark: string
    inline_code_mark: string
    thematic_break: string
    image: string
    mention: string
    mention_channel: string
    mention_emoji: string
    mention_ticker: string
    contract_address: string
}

export type MdastNodeType =
    | 'paragraph'
    | 'heading'
    | 'list'
    | 'listItem'
    | 'li'
    | 'lic'
    | 'link'
    | 'image'
    | 'blockquote'
    | 'code'
    | 'html'
    | 'emphasis'
    | 'strong'
    | 'underline'
    | 'delete'
    | 'inlineCode'
    | 'break'
    | 'thematicBreak'
    | 'text'
    | typeof MentionPlugin.key
    | typeof ELEMENT_MENTION_CHANNEL
    | typeof ELEMENT_MENTION_EMOJI
    | typeof ELEMENT_MENTION_TICKER
    | typeof ELEMENT_CONTRACT_ADDRESS

export const defaultNodeTypes: NodeTypes = {
    paragraph: 'p',
    block_quote: 'blockquote',
    code_block: CodeBlockPlugin.key,
    code_line: CodeLinePlugin.key,
    link: 'a',
    ul_list: 'ul',
    ol_list: 'ol',
    li: 'li',
    listItem: 'listItem',
    lic: 'lic',
    heading: {
        1: 'heading_one',
        2: 'heading_two',
        3: 'heading_three',
        4: 'heading_four',
        5: 'heading_five',
        6: 'heading_six',
    },
    emphasis_mark: 'italic',
    strong_mark: 'bold',
    underline_mark: 'underline',
    delete_mark: 'strikethrough',
    inline_code_mark: 'code',
    thematic_break: 'thematic_break',
    image: 'image',
    mention: MentionPlugin.key,
    mention_channel: ELEMENT_MENTION_CHANNEL,
    mention_emoji: ELEMENT_MENTION_EMOJI,
    mention_ticker: ELEMENT_MENTION_TICKER,
    contract_address: ELEMENT_CONTRACT_ADDRESS,
}

export interface LeafType {
    text: string
    strikethrough?: boolean
    underline?: boolean
    bold?: boolean
    italic?: boolean
    code?: boolean
    parentType?: string
}

export interface BlockType {
    type: string
    parentType?: string
    link?: string
    url?: string
    value?: string
    caption?: string
    lang?: string
    break?: boolean
    start?: number
    children: Array<BlockType | LeafType>
}

export interface InputNodeTypes {
    paragraph: string
    block_quote: string
    code_block: string
    link: string
    ul_list: string
    ol_list: string
    listItem: string
    heading: {
        1: string
        2: string
        3: string
        4: string
        5: string
        6: string
    }
    emphasis_mark: string
    strong_mark: string
    delete_mark: string
    inline_code_mark: string
    thematic_break: string
    image: string
}

type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>
}

export interface OptionType<T extends InputNodeTypes = InputNodeTypes> {
    nodeTypes?: RecursivePartial<T>
    linkDestinationKey?: string
    imageSourceKey?: string
    imageCaptionKey?: string
}

export interface MdastNode {
    type?: MdastNodeType
    ordered?: boolean
    value?: string
    text?: string
    children?: Array<MdastNode>
    depth?: 1 | 2 | 3 | 4 | 5 | 6
    url?: string
    alt?: string
    lang?: string
    meta?: string
    start?: number
    position?: { start: { offset: number }; end: { offset: number } }
    data?: { hName: string; hProperties: Record<string, string> }
    // spread?: any
    // checked?: any
    // indent?: any
    channel?: Record<string, string>
}

export type TextNode = { text?: string | undefined }

export type CodeBlockNode<T extends InputNodeTypes> = {
    type: T['code_block']
    lang: string | undefined
    children: Array<TextNode>
}

export type HeadingNode<T extends InputNodeTypes> = {
    type:
        | T['heading'][1]
        | T['heading'][2]
        | T['heading'][3]
        | T['heading'][4]
        | T['heading'][5]
        | T['heading'][6]
    children: Array<DeserializedNode<T>>
}

export type ListNode<T extends InputNodeTypes> = {
    type: T['ol_list'] | T['ul_list']
    children: Array<DeserializedNode<T>>
}

export type ListItemNode<T extends InputNodeTypes> = {
    type: T['listItem']
    children: Array<DeserializedNode<T>>
}

export type ParagraphNode<T extends InputNodeTypes> = {
    type: T['paragraph']
    break?: true
    children: Array<DeserializedNode<T>>
}

export type LinkNode<T extends InputNodeTypes> = {
    type: T['link']
    children: Array<DeserializedNode<T>>
    [urlKey: string]: string | undefined | Array<DeserializedNode<T>>
}

export type ImageNode<T extends InputNodeTypes> = {
    type: T['image']
    children: Array<DeserializedNode<T>>
    [sourceOrCaptionKey: string]: string | undefined | Array<DeserializedNode<T>>
}

export type BlockQuoteNode<T extends InputNodeTypes> = {
    type: T['block_quote']
    children: Array<DeserializedNode<T>>
}

export type InlineCodeMarkNode<T extends InputNodeTypes> = {
    type: T['inline_code_mark']
    children: Array<TextNode>
}

export type ThematicBreakNode<T extends InputNodeTypes> = {
    type: T['thematic_break']
    children: Array<DeserializedNode<T>>
}

export type ItalicNode<T extends InputNodeTypes> = {
    [K in T['emphasis_mark']]: true
} & {
    children: TextNode
}

export type BoldNode = {
    bold: true
    children: TextNode
}

export type StrikeThoughNode = {
    strikeThrough: true
    children: TextNode
}

export type InlineCodeNode = {
    code: true
    text: string | undefined
}

export type DeserializedNode<T extends InputNodeTypes> =
    | CodeBlockNode<T>
    | HeadingNode<T>
    | ListNode<T>
    | ListItemNode<T>
    | ParagraphNode<T>
    | LinkNode<T>
    | ImageNode<T>
    | BlockQuoteNode<T>
    | InlineCodeMarkNode<T>
    | ThematicBreakNode<T>
    | ItalicNode<T>
    | BoldNode
    | StrikeThoughNode
    | InlineCodeNode
    | TextNode
    | TCodeBlockElement
    | TChannelMentionElement
    | TMentionElement
    | TMentionEmoji
    | TMentionTicker
